import type {
  DeletionBlocker,
  DeletionCase,
  DeletionOutcome,
  DeletionState,
  DeletionStep,
} from "@/api/accountDeletion";
import { humanize } from "@/lib/format";

export const STEP_LABELS: Record<DeletionStep, string> = {
  wallet: "Wallet",
  cards: "Cards",
  ramps: "Transfers",
  earn: "Earn",
  providers: "External providers",
  warehouse: "Analytics and exports",
  backups: "Recovery backups",
  retention: "Data retention",
  identityRecords: "Identity records",
  receipt: "Final notice",
};

export const STATE_LABELS: Record<DeletionState, string> = {
  requested: "New request",
  awaitingUserAction: "Waiting for user",
  settling: "Under review",
  erasing: "Deletion in progress",
  completed: "Completed",
};

export function outcomeLabel(step: DeletionStep, outcome: DeletionOutcome | null): string {
  if (!outcome) return "Needs review";
  if (outcome === "ready") return step === "receipt" ? "Delivery not recorded" : "Work pending";
  if (outcome === "retained") return "Kept under policy";
  if (step === "receipt") return "Delivery recorded";
  if (step === "identityRecords") return "Deletion approved";
  if (step === "retention") return "Review complete";
  return "Deletion verified";
}

export function reviewTitle(step: DeletionStep): string {
  if (step === "identityRecords") return "Review identity records";
  if (step === "receipt") return "Final notice";
  return `Review ${STEP_LABELS[step].toLowerCase()}`;
}

export function reviewDescription(step: DeletionStep): string {
  switch (step) {
    case "identityRecords":
      return "Approve deletion of Elios identity records after the final checks, or choose an approved retention policy.";
    case "receipt":
      return "Send the final notice first, including any data kept and for how long. Then save proof of delivery here.";
    case "providers":
      return "Complete and verify the work with each provider first. Save the result here.";
    case "warehouse":
      return "Verify cleanup of analytics, logs and exports, or record why the data must be kept.";
    case "backups":
      return "Recovery backups are removed automatically. A saved review cannot replace confirmation from the wallet service.";
    case "retention":
      return "Record which data must be kept, under which approved policy, and until when.";
    default:
      return "Check funds, pending activity and records to keep. Save the verified result; this does not move funds.";
  }
}

export function nextAction(data: DeletionCase): string {
  if (data.request.state === "completed")
    return "Completed. Review any data kept when its review date is due.";
  if (data.canRecordFinalNotice) {
    if (data.blockers.some((item) => item.code === "staleFinalNotice"))
      return "Send an updated final notice, then record delivery.";
    const notice = data.reviews.find((item) => item.step === "receipt")?.evidence;
    return notice?.outcome === "erased" &&
      !data.blockers.some((item) => item.code === "finalNoticeDeliveryRequired")
      ? "Final notice recorded. The request will complete automatically."
      : "Send the final notice, then record delivery.";
  }
  if (data.canFinalize) return "Ready to start deletion.";
  if (data.request.state === "awaitingUserAction")
    return "Help the user resolve the account activity listed below.";
  if (
    data.request.state !== "erasing" &&
    currentBlockers(data).some((item) => item.code === "missingDomainAcknowledgment")
  )
    return "Waiting for account checks. Results will update automatically.";
  if (data.request.state === "erasing") {
    const checks = currentBlockers(data);
    if (
      checks.some((item) => ["domainNotClosed", "missingDomainAcknowledgment"].includes(item.code))
    )
      return "Account access is closing automatically. Wait for the checks to finish.";
    const rechecks = checks.filter((item) => item.code === "staleFinancialEvidence");
    if (rechecks.length)
      return `Review ${categoryList(rechecks)} again. Account checks changed after the last review.`;
    const reviews = checks.filter((item) =>
      ["missingEvidence", "invalidRetention", "terminalOutcomeRequired"].includes(item.code),
    );
    if (reviews.length) return `Finish the reviews for ${categoryList(reviews)}.`;
    if (!data.identityErasedAt)
      return checks.length
        ? "Check the items below before deletion can continue."
        : "Account and identity cleanup is automatic. Waiting for confirmation.";
    if (checks.some((item) => item.code === "recoveryCleanupPending"))
      return "Recovery backups are being removed automatically. Waiting for confirmation.";
    if (!checks.length) return "All steps are done. The request will complete automatically.";
    return "Check the remaining items below before deletion can finish.";
  }
  return "Complete the reviews below to prepare the account for deletion.";
}

export function currentBlockers(data: DeletionCase): DeletionBlocker[] {
  const phase =
    data.request.state === "erasing"
      ? data.identityErasedAt
        ? data.canRecordFinalNotice
          ? "completion"
          : "finalNotice"
        : "identityErasure"
      : "finalize";
  return data.request.state === "completed"
    ? []
    : data.blockers.filter((item) => item.phase === phase);
}

function categoryList(items: DeletionBlocker[]): string {
  const labels = [
    ...new Set(
      items
        .map((item) => item.step ?? item.domain)
        .map((step) => (step ? STEP_LABELS[step] : "the remaining items")),
    ),
  ];
  return new Intl.ListFormat("en", { style: "long", type: "conjunction" }).format(labels);
}

export function reviewNeedsRecheck(data: DeletionCase, step: DeletionStep): boolean {
  return currentBlockers(data).some(
    (item) =>
      item.code === "staleFinancialEvidence" && (item.step === step || item.domain === step),
  );
}

export function blockerLabel(item: DeletionBlocker): string {
  const category = categoryList([item]);
  switch (item.code) {
    case "missingEvidence":
      return `Review ${category}.`;
    case "invalidRetention":
      return `Update the retention policy and review date for ${category}.`;
    case "terminalOutcomeRequired":
      return `Record the final result for ${category}.`;
    case "missingDomainAcknowledgment":
      return `Waiting for ${category} to confirm the request.`;
    case "domainNotClosed":
      return `Waiting for ${category} access to close.`;
    case "staleFinancialEvidence":
      return `Review ${category} again. Its account checks changed since the last review.`;
    case "identityCleanupPending":
      return "Waiting for automatic account and identity cleanup.";
    case "recoveryCleanupPending":
      return "Waiting for automatic recovery-backup removal.";
    case "finalNoticeDeliveryRequired":
      return "Send the final notice, then record delivery.";
    case "staleFinalNotice":
      return "The reviews changed. Send an updated final notice and record delivery.";
    default:
      return item.message;
  }
}

const REASON_LABELS: Record<string, string> = {
  WALLET_WALLET_REVIEW: "Review wallets and balances",
  WALLET_SESSIONKEY_REVIEW: "Review session keys",
  WALLET_OWNEROPERATION_REVIEW: "Review pending owner operations",
  WALLET_PAYMENT_REVIEW: "Review payments and settlement",
  WALLET_OWNERKEYBACKUP_REVIEW: "Review recovery backups",
  CARDS_WALLETREF_REVIEW: "Review cardholder, cards and pending card activity",
  RAMPS_WALLETREF_REVIEW: "Review transfer accounts and pending transfers",
  EARN_WALLETREF_REVIEW: "Review Earn wallet records",
  EARN_EARNPOSITION_REVIEW: "Review Earn positions",
  EARN_EARNOPERATION_REVIEW: "Review pending Earn operations",
  EARN_VAULTAPPROVAL_REVIEW: "Review vault approvals",
};

export function reasonLabel(code: string): string {
  return REASON_LABELS[code] ?? humanize(code.toLowerCase());
}

export function actionError(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "The action could not be completed. Refresh the case before retrying.";
}

export function localDateTime(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

export function isFutureDate(value: string, now = Date.now()): boolean {
  const time = new Date(value).getTime();
  return Number.isFinite(time) && time > now;
}
